import React, { useState, useEffect } from 'react';
import { Card, Input, Empty, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FeatherIcon from 'feather-icons-react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { RootState } from 'store/rootReducers';
import { fetchProjects } from 'store/projects/projectsSlice';
import { ProjectDto } from 'api/models';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchInput = styled(Input)`
  width: 84%;
  margin-bottom: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
  width: 100%;
`;

const ProjectGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const ProjectCard = styled(Card)<{ $selected?: boolean }>`
  cursor: pointer;
  transition: all 0.3s;
  flex: 1;
  min-width: 250px;
  max-width: calc(33.333% - 11px);
  border: 2px solid ${props => (props.$selected ? '#1890ff' : '#d9d9d9')};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  }

  @media (max-width: 992px) {
    max-width: calc(50% - 8px);
  }

  @media (max-width: 576px) {
    max-width: 100%;
  }
`;

const MetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

interface StateProps {
  projects: ProjectDto[];
  loading: boolean;
}

interface DispatchProps {
  fetchProjects: () => void;
}

interface OwnProps {
  value?: number;
  onChange: (projectId: number) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const ProjectSelection: React.FC<Props> = ({ value, onChange, projects, loading, fetchProjects }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects();
    }
  }, [fetchProjects, projects.length]);

  const filteredProjects = projects.filter((project: ProjectDto) =>
    project.name!.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Container>
      <SearchInput
        prefix={<FeatherIcon icon="search" size={16} />}
        placeholder={t('projects:search', 'Search projects...')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
      />

      {loading ? (
        <LoadingContainer>
          <Spin style={{ width: '100%' }} />
        </LoadingContainer>
      ) : filteredProjects.length > 0 ? (
        <ProjectGrid>
          {filteredProjects.map((project: ProjectDto) => (
            <ProjectCard key={project.id} $selected={project.id === value} onClick={() => onChange(project.id!)}>
              <Card.Meta
                title={project.name}
                description={
                  <MetaContainer>
                    <MetaItem>
                      <FeatherIcon icon="box" size={16} />
                      {t('projects:units', 'Units')}: {project.unitsCount}
                    </MetaItem>
                  </MetaContainer>
                }
              />
              {project.id === value && (
                <CheckIcon>
                  <FeatherIcon icon="check-circle" size={16} color="#1890ff" />
                </CheckIcon>
              )}
            </ProjectCard>
          ))}
        </ProjectGrid>
      ) : (
        <Empty description={t('projects:no-results', 'No projects found')} />
      )}
    </Container>
  );
};

const mapStateToProps = (state: RootState): StateProps => ({
  projects: state.projects.items.filter(project => project.isActive && project.unitsCount! > 0),
  loading: state.projects.loading,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, unknown, AnyAction>): DispatchProps => ({
  fetchProjects: () => dispatch(fetchProjects()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectSelection);
